import { message, Upload } from 'antd'
import server from 'util/Server'
import { RecursoMetadata, TIPOS_RECURSO } from '../interfaces/ConfiguracionRecursos.interfaces'

export type ModoRecurso = 'crear' | 'editar' | 'ver'

/**
 * Completa la url del recurso con el host multimedia
 * (REACT_APP_HOST_MULTIMEDIA). El backend guarda la ruta que le configuraron en
 * RECURSOS_STORAGE_PUBLIC_URL, que es relativa, así que el host lo pone el front.
 *
 * Se usa SOLO para mostrar/abrir el archivo: en el form se conserva la url tal
 * como la devolvió el backend, porque es la que hay que reenviar al editar.
 */
export const construirUrlRecurso = (url?: string | null): string => {
  if (!url) return ''
  return server.host + url
}

export const TITULOS_MODAL_RECURSO: Record<ModoRecurso, string> = {
  crear: 'Nuevo recurso',
  editar: 'Editar recurso',
  ver: 'Detalle del recurso',
}

export const getSubtituloModal = (modo: ModoRecurso, step: 1 | 2) => {
  if (modo !== 'crear') {
    return 'Guarda un enlace a una imagen o video para usarlo en las pantallas.'
  }
  return step === 1
    ? 'Elige la organización o la regional para autoseleccionar sus estaciones, o selecciónalas manualmente.'
    : 'Completa los datos del recurso.'
}

/**
 * Devuelve un validador `beforeUpload` para el componente `Upload` de antd,
 * acotado al tipo de recurso (imagen/video) seleccionado.
 */
export const buildBeforeUpload = (tipoId: number) => (file: File) => {
  const esVideo = tipoId === TIPOS_RECURSO.VIDEO
  const tiposValidos = esVideo ? ['video/mp4'] : ['image/png', 'image/webp']

  if (!tiposValidos.includes(file.type)) {
    message.error(esVideo ? 'Solo se permiten archivos .mp4' : 'Solo se permiten archivos .png o .webp')
    return Upload.LIST_IGNORE
  }
  return true
}

/**
 * Lee en el navegador los datos técnicos del archivo (dimensiones, duración,
 * peso) sin subirlo, solo para mostrarlos en el preview. Si el archivo no se
 * puede decodificar se resuelve igual con la metadata básica en lugar de
 * rechazar la promesa.
 */
export const extraerMetadataArchivo = (file: File): Promise<RecursoMetadata> => {
  const base: RecursoMetadata = {
    nombre_archivo: file.name,
    tipo_mime: file.type,
    extension: file.name.split('.').pop()?.toLowerCase() ?? '',
    peso_bytes: file.size,
  }

  const objectUrl = URL.createObjectURL(file)

  return new Promise((resolve) => {
    const finalizar = (extra: Partial<RecursoMetadata>) => {
      URL.revokeObjectURL(objectUrl)
      resolve({ ...base, ...extra })
    }

    if (file.type.startsWith('video/')) {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = () =>
        finalizar({
          ancho: video.videoWidth,
          alto: video.videoHeight,
          duracion_segundos: Number.isFinite(video.duration) ? Math.round(video.duration) : undefined,
        })
      video.onerror = () => finalizar({})
      video.src = objectUrl
      return
    }

    const imagen = new Image()
    imagen.onload = () => finalizar({ ancho: imagen.naturalWidth, alto: imagen.naturalHeight })
    imagen.onerror = () => finalizar({})
    imagen.src = objectUrl
  })
}

/** Peso del archivo en un formato legible para el preview (ej. "6,2 MB"). */
export const formatearPeso = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}